import React, { createContext, useState, useEffect, ReactNode } from 'react';

interface TokenMapContextProps {
  tokenMap: { [gameId: number]: string };
  nextTokenExpiration: { gameId?: number; expiresAt?: string };
  saveToken: (gameId: number, token: string, expiresAt: string) => void;
  removeToken: (gameId: number) => void;
  removeNextTokenExpiration: () => void;
  loadTokens: () => void;
}

export const TokenMapContext = createContext<TokenMapContextProps | undefined>(undefined);

export const TokenMapProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [tokenMap, setTokenMap] = useState<{ [gameId: number]: string }>({});
  const [nextTokenExpiration, setNextTokenExpiration] = useState<{ gameId?: number; expiresAt?: string }>({});

  const loadTokensFromLocalStorage = () => {
    const storedTokens = localStorage.getItem('reservationTokens');
    const storedNextTokenExpiration = localStorage.getItem('nextTokenExpiration');
    if (storedTokens) {
      setTokenMap(JSON.parse(storedTokens));
    }
    if (storedNextTokenExpiration) {
      setNextTokenExpiration(JSON.parse(storedNextTokenExpiration));
    }
  };

  const saveTokenToLocalStorage = (gameId: number, token: string, expiresAt: string) => {
    const updatedTokens = { ...tokenMap, [gameId]: token };
    setTokenMap(updatedTokens);
    localStorage.setItem('reservationTokens', JSON.stringify(updatedTokens));

    const nextTokenExpirationData = { gameId, expiresAt };
    setNextTokenExpiration(nextTokenExpirationData);
    localStorage.setItem('nextTokenExpiration', JSON.stringify(nextTokenExpirationData));
  };

  const removeTokenFromLocalStorage = (gameId: number) => {
    const updatedTokens = { ...tokenMap };
    delete updatedTokens[gameId];
    setTokenMap(updatedTokens);
    setNextTokenExpiration({ gameId: undefined, expiresAt: undefined });
    localStorage.setItem('reservationTokens', JSON.stringify(updatedTokens));
    localStorage.removeItem('nextTokenExpiration');
  };

  const removeNextTokenExpirationFromLocalStorage = () => {
    localStorage.removeItem('nextTokenExpiration');
  };

  useEffect(() => {
    loadTokensFromLocalStorage();

    // Optionally, add a storage event listener to handle changes from other tabs
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === 'reservationTokens' || event.key === 'nextTokenExpiration') {
        loadTokensFromLocalStorage();
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  return (
    <TokenMapContext.Provider
      value={{
        tokenMap,
        nextTokenExpiration,
        saveToken: saveTokenToLocalStorage,
        removeToken: removeTokenFromLocalStorage,
        removeNextTokenExpiration: removeNextTokenExpirationFromLocalStorage,
        loadTokens: loadTokensFromLocalStorage,
      }}
    >
      {children}
    </TokenMapContext.Provider>
  );
};
